import features from 'src/replicant/ruleset/features';
import { Pet } from '../pet';
import { Fate } from './';
import {
  hasPermanentStatusEffect,
  isSafeTime,
  hasDeathFeatureActivated,
} from './fate.getters';
import { VisitRecord } from '../../visit/record';
import {
  sickCooldownInterval,
  dyingCooldownInterval,
  toothDecayCooldownInterval,
  scaredCooldownInterval,
} from './fate.ruleset';
import { getLocalTimezone } from 'src/replicant/utils/time';

/**
 * Detect if pet will want to go out with given input params
 */
export const shouldWantToGoOut = (
  state: Fate.State,
  random: number,
  now: number,
) => {
  // Feature not unlocked yet.
  if (!VisitRecord.getters.isUnlocked(state)) {
    return false;
  }

  // Pet not available
  const currentEvolution = Pet.getters(state).getCurrentEvolution();
  if (!currentEvolution) {
    return false;
  }

  const petCreatedAt = Pet.getters(state).getPetCreatedAt();
  const lastVisitTimestamp = VisitRecord.getters.getLastVisitTimestamp(state);
  const timeSinceLastVisit = Pet.ruleset.getAwakeDuration(
    currentEvolution,
    petCreatedAt,
    lastVisitTimestamp,
    now,
    state,
  );
  return Pet.ruleset.shouldWantToGoOut(timeSinceLastVisit, random);
};

/**
 * Detect if pet will be sick with given input params
 */
export function shouldGetSick(
  state: Fate.State,
  random: number,
  now: number,
  since: number,
): boolean {
  const { getPetCreatedAt, getStressValue } = Pet.getters(state);

  const isPetExist = !!getPetCreatedAt();
  if (!isPetExist) return false;

  // If death feature enabled
  if (hasDeathFeatureActivated(state, since)) {
    const { lastUpdatedAt } = state.pet.statusEffects['sick'];
    const delta = now - lastUpdatedAt;

    // Do not get sick after two hours after previous sick
    if (lastUpdatedAt && delta < sickCooldownInterval) return false;

    const stress = getStressValue(now);

    // Sick probability = MAX(stress - 30, 0) / 1000
    const probability = Math.max(stress - 30, 0) / 1000;
    const isSick = random < probability;

    return isSick;
  } else {
    const stress = getStressValue(now);
    const isSick = random < stress / 1000;

    return isSick;
  }
}

/**
 * Detect if pet will have tooth decay with given input params
 */
export function shouldGetToothDecay(
  state: Fate.State,
  random: number,
  now: number,
  since: number,
): boolean {
  const { getPetCreatedAt, getAttributeValue } = Pet.getters(state);

  const isPetExist = !!getPetCreatedAt();
  if (!isPetExist) return false;

  // If death feature enabled
  if (hasDeathFeatureActivated(state, since)) {
    const { lastUpdatedAt } = state.pet.statusEffects['toothDecay'];
    const delta = now - lastUpdatedAt;

    // Do not get tooth decay after two hours after previous tooth decay resolve
    if (lastUpdatedAt && delta < toothDecayCooldownInterval) return false;

    const toothAcidity = getAttributeValue('toothAcidity', now);
    const probability = Math.max(toothAcidity - 30, 0) / 1000;
    const hasToothDecay = random < probability;

    return hasToothDecay;
  } else {
    const toothAcidity = getAttributeValue('toothAcidity', now);
    const hasToothDecay = random < toothAcidity / 1000;

    return hasToothDecay;
  }
}

/**
 * Detect if pet will be scared with given input params
 */
export function shouldGetScared(
  state: Fate.State,
  random: number,
  now: number,
  since: number,
): boolean {
  const {
    getCurrentEvolution,
    getAttributeValue,
    getPetCreatedAt,
  } = Pet.getters(state);

  const isPetExist = !!getPetCreatedAt();
  if (!isPetExist) return false;

  const species = getCurrentEvolution().species;
  if (!species) return false;

  const love = {
    current: getAttributeValue('love', now),
    max: Pet.ruleset.attributeBounds.max,
  };

  // If death feature activated
  if (hasDeathFeatureActivated(state, since)) {
    const { lastUpdatedAt } = state.pet.statusEffects['scared'];
    const delta = now - lastUpdatedAt;

    // Do not get scared after two hours after previous scared resolve
    if (lastUpdatedAt && delta < scaredCooldownInterval) return false;

    const coefficient = Pet.ruleset.scaredCoefficients[species];
    const loveDelta = love.max - love.current;
    const probability = (coefficient * Math.max(loveDelta - 30, 0)) / 1000;
    const isScared = random < probability;

    return isScared;
  } else {
    const coefficient = Pet.ruleset.scaredCoefficients[species];
    const isScared = random < (coefficient * (love.max - love.current)) / 1000;

    return isScared;
  }
}

/**
 * Detect if pet will be sleeping with given input params
 */
export function shouldBeAsleep(state: Fate.State, now: number): boolean {
  const { getCurrentEvolution } = Pet.getters(state);

  return Pet.ruleset.shouldBeAsleep(getCurrentEvolution(), now, state);
}

/**
 * Detect if pet will be evolving with given input params
 */
export function shouldEvolve(state: Fate.State, now: number): boolean {
  const { getCurrentEvolution } = Pet.getters(state);

  const lastEvolution = getCurrentEvolution();
  if (!lastEvolution) return false;

  const { timestamp, stage } = lastEvolution;

  // If time to evolve
  if (now - timestamp >= Pet.ruleset.timeToEvolve[stage]) {
    return true;
  }

  return false;
}

/**
 * Detect if we should get dying status
 *
 * While the pet is in sick status, determine if the pet will get dying or not based on the dying probability at 5 minute intervals
 * Dying probability is calculated by the formula below
 * Dying probability = stress / 1000 + pet_age / 100
 * For example, if stress value is 70 and the pet age is 3, the dying probability will be 70 / 1000 + 3 / 100 = 0.10 = 10%
 */
export function shouldGetDying(
  state: Fate.State,
  random: number,
  now: number,
  since: number,
) {
  // Is death feature activated for this time
  if (!hasDeathFeatureActivated(state, since)) return false;

  const { getStressValue, getPetAge } = Pet.getters(state);

  const isSick = hasPermanentStatusEffect(
    state,
    'sick',
    now - Fate.ruleset.dyingMoratoriumInterval,
  );

  // Only if pet sick
  if (!isSick) return false;

  // Keep dying cooldown
  // Do not get dying after three hours after previous dying resolve
  const dyingLastUpdatedAt = state.pet.statusEffects['dying'].lastUpdatedAt;
  const dyingDelta = now - dyingLastUpdatedAt;
  if (dyingLastUpdatedAt && dyingDelta < dyingCooldownInterval) return false;

  // Keep sick cooldown
  // Do not get dying after three hours after previous sick resolve
  const sickLastUpdatedAt = state.pet.statusEffects['sick'].lastUpdatedAt;
  const sickDelta = now - sickLastUpdatedAt;
  if (sickLastUpdatedAt && sickDelta < dyingCooldownInterval) return false;

  // Calculate probability
  const stress = getStressValue(now);
  const age = getPetAge(now);
  const probability = Math.max(stress - 30, 0) / 1000 + age / 500;

  // Detect if we hit probability
  if (probability > random) return true;

  return false;
}

/**
 * Detect if we should get dead status
 *
 * While the pet is in dying status, determine if the pet will die or not based on the death probability at 5 minute intervals
 * Death probability is calculated by the formula below
 * Death probability = stress / 1000 + pet_age / 100
 * For example, if stress value is 70 and the pet age is 3, the death probability will be 70 / 1000 + 3 / 100 = 0.10 = 10%
 */
export function shouldGetDead(
  state: Fate.State,
  random: number,
  now: number,
  since: number,
) {
  // Is death feature activated for this time
  if (!hasDeathFeatureActivated(state, since)) return false;

  const { getStressValue, getPetAge } = Pet.getters(state);

  const isDying = hasPermanentStatusEffect(
    state,
    'dying',
    now - Fate.ruleset.deadMoratoriumInterval,
  );
  const stress = getStressValue(now);
  const age = getPetAge(now);

  // Only if pet dying
  if (!isDying) return false;

  // Pet can't die between 10pm and 9am
  const timeZone = getLocalTimezone(state);
  if (isSafeTime(now, timeZone)) return false;

  // Calculate probability
  const probability = Math.max(stress - 30, 0) / 1000 + age / 500;

  // Detect if we hit probability
  if (probability > random) return true;

  return false;
}
